import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, UserCheck, UserX, Clock, Shield, LogOut, Menu, X, Search,
  CheckCircle, XCircle, AlertTriangle, Eye, Mail, Phone, MapPin, 
  Calendar, Activity, ChevronRight, RefreshCw, Ban, RotateCcw, AlertCircle, Key
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// ==================== ADMIN CONTEXT ====================
const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error("useAdmin must be used within AdminProvider");
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("doctodays_admin_token"));

  useEffect(() => {
    if (token) {
      setAdmin({ email: "admin@doctodays.com" });
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/admin/login`, { email, password });
    localStorage.setItem("doctodays_admin_token", res.data.token);
    setToken(res.data.token);
    setAdmin({ email: res.data.email });
  };

  const logout = () => {
    localStorage.removeItem("doctodays_admin_token");
    setToken(null);
    setAdmin(null);
  };

  return (
    <AdminContext.Provider value={{ admin, token, login, logout }}>
      {children}
    </AdminContext.Provider>
  );
};

// ==================== LOGO COMPONENT ====================
import { Logo } from "./components/Logo";

// ==================== ADMIN NAVBAR ====================
const AdminNavbar = () => {
  const { logout } = useAdmin();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <nav className="bg-slate-900 text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Logo size="small" />
            <div>
              <span className="font-bold text-lg">DoctoDays</span>
              <span className="ml-2 px-2 py-0.5 bg-amber-500 text-xs rounded-full font-medium">ADMIN</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-400 hover:text-white text-sm">
              Voir le site
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// ==================== ADMIN LOGIN PAGE ====================
export const AdminLoginPage = () => {
  const { login, admin } = useAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin) {
      navigate("/admin/dashboard");
    }
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-8 bg-gradient-to-r from-slate-800 to-slate-900 text-center">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-white">Administration</h1>
          <p className="text-slate-400 mt-1">Espace de gestion DoctoDays</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email administrateur</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 outline-none"
              placeholder="admin@doctodays.com"
              data-testid="admin-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 outline-none"
              placeholder="••••••••"
              data-testid="admin-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-semibold hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Shield className="w-5 h-5" /> Connexion Admin
              </>
            )}
          </button>

          <div className="text-center">
            <Link
              to="/admin/forgot-password"
              className="text-sm text-slate-500 hover:text-slate-700"
              data-testid="admin-forgot-password"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="text-center pt-4 border-t">
            <Link to="/" className="text-slate-500 text-sm hover:text-emerald-600">
              ← Retour au site
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ==================== ADMIN FORGOT PASSWORD PAGE ====================
export const AdminForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email,
        account_type: "admin"
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-8 bg-gradient-to-r from-slate-800 to-slate-900 text-center">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-white">Mot de passe oublié</h1>
          <p className="text-slate-400 mt-1">Administration Doctodays</p>
        </div>

        <div className="p-8">
          {sent ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-slate-600">
                  Si l'adresse <strong>{email}</strong> correspond au compte administrateur, un email a été envoyé.
                </p>
              </div>
              <Link
                to="/admin/login"
                className="inline-block px-6 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email administrateur</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 outline-none"
                  placeholder="admin@doctodays.com"
                  data-testid="admin-forgot-email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-semibold hover:bg-slate-800 transition-all disabled:opacity-50"
                data-testid="admin-forgot-submit"
              >
                {loading ? "Envoi..." : "Envoyer le lien de réinitialisation"}
              </button>

              <div className="text-center">
                <Link to="/admin/login" className="text-slate-600 hover:text-slate-800 text-sm">
                  ← Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ==================== ADMIN RESET PASSWORD PAGE ====================
export const AdminResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate("/admin/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/admin/forgot-password" className="text-slate-700 hover:underline">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-8 bg-gradient-to-r from-slate-800 to-slate-900 text-center">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            {success ? "Mot de passe réinitialisé !" : "Nouveau mot de passe"}
          </h1>
        </div>

        <div className="p-8">
          {success ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-slate-600">
                Votre mot de passe a été modifié avec succès.<br />
                Redirection vers la connexion...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 outline-none"
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="admin-new-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Confirmer le mot de passe</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-500 outline-none"
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="admin-confirm-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-semibold hover:bg-slate-800 transition-all disabled:opacity-50"
                data-testid="admin-reset-submit"
              >
                {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ==================== PRACTITIONER CARD ====================
const PractitionerCard = ({ practitioner, onValidate, onReject, onSuspend, onReactivate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [reason, setReason] = useState("");

  const statusBadge = () => {
    if (practitioner.is_suspended) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Suspendu</span>;
    }
    if (practitioner.is_verified) {
      return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Vérifié</span>;
    }
    return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">En attente</span>;
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`practitioner-card-${practitioner.id}`}>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <img
              src={practitioner.photo_url || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100"}
              alt={`Dr. ${practitioner.last_name}`}
              className="w-16 h-16 rounded-xl object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-slate-900">Dr. {practitioner.first_name} {practitioner.last_name}</h3>
                {statusBadge()}
                {practitioner.rpps_verified && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <Shield className="w-3 h-3" /> RPPS
                  </span>
                )}
              </div>
              <p className="text-emerald-600 text-sm">{practitioner.specialty}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {practitioner.city}</span>
                {practitioner.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {practitioner.phone}</span>}
              </div>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Mail className="w-3 h-3" /> {practitioner.email}
              </p>
              {practitioner.rpps_number && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> RPPS: {practitioner.rpps_number}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            {!practitioner.is_verified && !practitioner.is_suspended && (
              <>
                <button
                  onClick={() => onValidate(practitioner.id)}
                  disabled={loading}
                  className="flex-1 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium disabled:opacity-50"
                  data-testid={`validate-${practitioner.id}`}
                >
                  <CheckCircle className="w-4 h-4" /> Valider
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={loading}
                  className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium disabled:opacity-50"
                  data-testid={`reject-${practitioner.id}`}
                >
                  <XCircle className="w-4 h-4" /> Rejeter
                </button>
              </>
            )}
            {practitioner.is_verified && !practitioner.is_suspended && (
              <button
                onClick={() => setShowSuspendModal(true)}
                disabled={loading}
                className="flex-1 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium disabled:opacity-50"
                data-testid={`suspend-${practitioner.id}`}
              >
                <Ban className="w-4 h-4" /> Suspendre
              </button>
            )}
            {practitioner.is_suspended && (
              <button
                onClick={() => onReactivate(practitioner.id)}
                disabled={loading}
                className="flex-1 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium disabled:opacity-50"
                data-testid={`reactivate-${practitioner.id}`}
              >
                <RotateCcw className="w-4 h-4" /> Réactiver
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-4 border-b border-slate-100 bg-red-50">
                <h3 className="font-semibold text-red-900 flex items-center gap-2">
                  <XCircle className="w-5 h-5" /> Rejeter l'inscription
                </h3>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-slate-600">
                  Êtes-vous sûr de vouloir rejeter l'inscription de <strong>Dr. {practitioner.first_name} {practitioner.last_name}</strong> ?
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motif du refus (optionnel)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-red-500 outline-none"
                    placeholder="Informations incomplètes, spécialité non vérifiable..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowRejectModal(false); setReason(""); }}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => { onReject(practitioner.id, reason); setShowRejectModal(false); setReason(""); }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Confirmer le rejet
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Suspend Modal */}
      <AnimatePresence>
        {showSuspendModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-4 border-b border-slate-100 bg-amber-50">
                <h3 className="font-semibold text-amber-900 flex items-center gap-2">
                  <Ban className="w-5 h-5" /> Suspendre le compte
                </h3>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-slate-600">
                  Êtes-vous sûr de vouloir suspendre le compte de <strong>Dr. {practitioner.first_name} {practitioner.last_name}</strong> ?
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motif de suspension (optionnel)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-amber-500 outline-none"
                    placeholder="Signalements de patients, informations erronées..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowSuspendModal(false); setReason(""); }}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => { onSuspend(practitioner.id, reason); setShowSuspendModal(false); setReason(""); }}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    Confirmer la suspension
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== ADMIN DASHBOARD PAGE ====================
export const AdminDashboardPage = () => {
  const { admin, token } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [slotDays, setSlotDays] = useState(60);
  const [slotDaysInput, setSlotDaysInput] = useState("60");
  const [slotActionLoading, setSlotActionLoading] = useState(false);
  const [subStats, setSubStats] = useState(null);

  useEffect(() => {
    if (!admin) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [admin, navigate, filter]);

  const fetchData = async () => {
    try {
      const [statsRes, practitionersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/practitioners?status=${filter}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setPractitioners(practitionersRes.data);
      // Load slot generation setting
      try {
        const sRes = await axios.get(`${API_URL}/admin/settings/slot-days`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSlotDays(sRes.data.days);
        setSlotDaysInput(String(sRes.data.days));
      } catch {}
      // Load subscription stats
      try {
        const subRes = await axios.get(`${API_URL}/admin/subscriptions/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubStats(subRes.data);
      } catch {}
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/admin/practitioners/${id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert("Erreur lors de la validation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/admin/practitioners/${id}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert("Erreur lors du rejet");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async (id, reason) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/admin/practitioners/${id}/suspend`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert("Erreur lors de la suspension");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (id) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/admin/practitioners/${id}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      alert("Erreur lors de la réactivation");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPractitioners = practitioners.filter(p =>
    `${p.first_name} ${p.last_name} ${p.email} ${p.specialty} ${p.city}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Tableau de bord Admin</h1>
          <p className="text-slate-600">Gérez les praticiens et surveillez l'activité</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: "En attente", value: stats.pending_practitioners, color: "amber", icon: <Clock className="w-5 h-5" /> },
              { label: "Vérifiés", value: stats.verified_practitioners, color: "emerald", icon: <UserCheck className="w-5 h-5" /> },
              { label: "Suspendus", value: stats.suspended_practitioners, color: "red", icon: <Ban className="w-5 h-5" /> },
              { label: "Total Praticiens", value: stats.total_practitioners, color: "slate", icon: <Users className="w-5 h-5" /> },
              { label: "Patients", value: stats.total_patients, color: "blue", icon: <Users className="w-5 h-5" /> },
              { label: "Rendez-vous", value: stats.total_appointments, color: "purple", icon: <Calendar className="w-5 h-5" /> },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className={`text-${stat.color}-600 mb-2`}>{stat.icon}</div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-slate-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Business cards concierge — quick access */}
        <Link
          to="/admin/business-cards"
          className="block bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-200 rounded-xl p-5 mb-6 hover:shadow-md transition-shadow"
          data-testid="admin-bizcards-link"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center text-lg">🎁</div>
              <div>
                <h3 className="font-bold text-slate-900">Cartes de visite — Demandes</h3>
                <p className="text-sm text-slate-600">Voir et traiter les commandes "100 cartes offertes".</p>
              </div>
            </div>
            <span className="text-emerald-700 font-semibold text-sm">Gérer les demandes →</span>
          </div>
        </Link>

        {/* GDPR / Legal pack download */}
        <button
          type="button"
          onClick={async () => {
            try {
              const res = await axios.get(`${API_URL}/admin/legal-docs.zip`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
              });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
              const link = document.createElement("a");
              link.href = url;
              link.download = `doctodays-legal-pack-${new Date().toISOString().slice(0,10)}.zip`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            } catch {
              alert("Téléchargement impossible. Vérifiez votre session admin.");
            }
          }}
          className="w-full text-left bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-5 mb-6 hover:shadow-md transition-shadow"
          data-testid="admin-legal-download"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center text-lg">📜</div>
              <div>
                <h3 className="font-bold">Pack de documents RGPD</h3>
                <p className="text-sm text-slate-300">Télécharger registre des traitements, AIPD, procédure 72h, mentions légales.</p>
              </div>
            </div>
            <span className="text-emerald-300 font-semibold text-sm">Télécharger le ZIP →</span>
          </div>
        </button>

        {/* MongoDB backup download */}
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(
              "Générer une sauvegarde complète de la base de données ?\n\n" +
              "⚠️ Le fichier ZIP contient TOUTES les données production (patients, praticiens, RDV).\n" +
              "Chiffrez-le (7-Zip AES-256) avant de le stocker et supprimez-le une fois inutile (RGPD)."
            )) return;
            try {
              const res = await axios.get(`${API_URL}/admin/db-backup.zip`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
                timeout: 180000,
              });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
              const link = document.createElement("a");
              link.href = url;
              const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
              link.download = `doctodays-db-backup-${stamp}.zip`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              const status = e?.response?.status;
              const detail = e?.response?.data?.detail
                || (e?.response?.data instanceof Blob
                    ? await e.response.data.text().catch(() => "")
                    : "")
                || e?.message
                || "erreur inconnue";
              alert(
                "Sauvegarde impossible.\n\n" +
                (status ? `HTTP ${status}\n` : "") +
                `Détail: ${String(detail).slice(0, 500)}\n\n` +
                "Astuce: ouvrez la console navigateur (F12) → onglet Réseau, cliquez à nouveau, et regardez la réponse de /api/admin/db-backup.zip."
              );
            }
          }}
          className="w-full text-left bg-gradient-to-br from-emerald-900 to-emerald-700 text-white rounded-xl p-5 mb-6 hover:shadow-md transition-shadow"
          data-testid="admin-db-backup-download"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/20 text-white flex items-center justify-center text-lg">💾</div>
              <div>
                <h3 className="font-bold">Sauvegarde base de données</h3>
                <p className="text-sm text-emerald-100">Générer un ZIP complet MongoDB (export natif) — à chiffrer et stocker hors-ligne.</p>
              </div>
            </div>
            <span className="text-emerald-200 font-semibold text-sm">Télécharger le backup →</span>
          </div>
        </button>

        {/* Medical keyword filter — quick access */}
        <Link
          to="/admin/medical-keywords"
          className="block bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 hover:shadow-md transition-shadow"
          data-testid="admin-medical-keywords-link"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-amber-500 text-white flex items-center justify-center text-lg">⚠️</div>
              <div>
                <h3 className="font-bold text-slate-900">Filtre médical du chat</h3>
                <p className="text-sm text-slate-600">Liste des mots-clés bloqués automatiquement dans la messagerie patient↔soignant.</p>
              </div>
            </div>
            <span className="text-amber-700 font-semibold text-sm">Gérer la liste →</span>
          </div>
        </Link>

        {/* Subscription stats */}
        {subStats && (
          <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm" data-testid="admin-sub-stats">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex w-5 h-5 items-center justify-center text-emerald-600 font-bold">€</span>
              <h3 className="font-bold text-slate-800">Abonnements praticiens</h3>
              <span className="ml-auto text-sm text-slate-500">
                MRR : <strong className="text-emerald-700">{subStats.mrr_eur} €</strong> /mois
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
              {[
                { key: "trial", label: "En essai", color: "blue" },
                { key: "active", label: "Actifs", color: "emerald" },
                { key: "past_due", label: "En grâce", color: "amber" },
                { key: "suspended", label: "Suspendus", color: "red" },
                { key: "cancelled", label: "Annulés", color: "slate" },
                { key: "exempted", label: "Offerts", color: "purple" },
              ].map((c) => (
                <div key={c.key} className={`rounded-lg bg-${c.color}-50 border border-${c.color}-100 py-3`}>
                  <p className={`text-2xl font-bold text-${c.color}-700`}>{subStats.counts?.[c.key] ?? 0}</p>
                  <p className={`text-xs text-${c.color}-700/80`}>{c.label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Plans : {subStats.monthly_price_eur} € / mois · {subStats.yearly_price_eur} € / an
            </p>
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
              <button
                onClick={async () => {
                  if (!window.confirm(
                    "Attribuer un statut d'abonnement à TOUS les praticiens qui n'en ont pas encore ?\n\n" +
                    "• Ceux inscrits avant le 24/06/2026 → Offerts (gratuit à vie)\n" +
                    "• Ceux inscrits après → Essai 6 mois\n\n" +
                    "Les praticiens ayant déjà un statut ne seront PAS modifiés. Action sûre et idempotente."
                  )) return;
                  try {
                    const res = await axios.post(`${API_URL}/admin/subscriptions/backfill`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    const d = res.data || {};
                    alert(
                      `Backfill terminé ✓\n\n` +
                      `Total praticiens : ${d.total}\n` +
                      `Offerts (grandfathered) : ${d.exempted}\n` +
                      `Mis en essai 6 mois : ${d.trial}\n` +
                      `Déjà configurés (ignorés) : ${d.already_set}`
                    );
                    fetchData();
                  } catch (err) {
                    alert("Erreur lors du backfill : " + (err.response?.data?.detail || err.message));
                  }
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm inline-flex items-center gap-2"
                data-testid="admin-backfill-btn"
              >
                Initialiser les statuts d'abonnement
              </button>
              <span className="text-xs text-slate-500">
                À exécuter une fois après le lancement du paiement pour exempter les praticiens déjà inscrits.
              </span>
            </div>
          </div>
        )}

        {/* Settings: slot generation duration */}
        <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-slate-800">Durée des créneaux disponibles</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Définit sur combien de jours à l'avance les patients peuvent prendre rendez-vous. Affecte les médecins démo (les praticiens gèrent leurs propres créneaux).
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre de jours (7 à 365)</label>
              <input
                type="number"
                min={7}
                max={365}
                value={slotDaysInput}
                onChange={(e) => setSlotDaysInput(e.target.value)}
                className="w-32 px-3 py-2 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none"
                data-testid="admin-slot-days-input"
              />
            </div>
            <button
              onClick={async () => {
                const days = parseInt(slotDaysInput, 10);
                if (!days || days < 7 || days > 365) { alert("Entrez un nombre entre 7 et 365"); return; }
                setSlotActionLoading(true);
                try {
                  await axios.put(`${API_URL}/admin/settings/slot-days`, { days }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setSlotDays(days);
                  alert(`Configuration enregistrée (${days} jours). Cliquez sur "Régénérer maintenant" pour l'appliquer immédiatement.`);
                } catch {
                  alert("Erreur lors de l'enregistrement");
                } finally { setSlotActionLoading(false); }
              }}
              disabled={slotActionLoading}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium disabled:opacity-50"
              data-testid="admin-save-slot-days"
            >
              Enregistrer
            </button>
            <button
              onClick={async () => {
                const days = parseInt(slotDaysInput, 10);
                if (!days || days < 7 || days > 365) { alert("Entrez un nombre entre 7 et 365"); return; }
                if (!window.confirm(`Régénérer les créneaux de TOUS les médecins démo sur ${days} jours ? Les créneaux actuels seront remplacés.`)) return;
                setSlotActionLoading(true);
                try {
                  const res = await axios.post(`${API_URL}/admin/regenerate-all-slots?days=${days}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  alert(`${res.data.doctors_updated} médecins régénérés.\nPériode : ${res.data.date_range?.first} → ${res.data.date_range?.last}`);
                  setSlotDays(days);
                } catch {
                  alert("Erreur lors de la régénération");
                } finally { setSlotActionLoading(false); }
              }}
              disabled={slotActionLoading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50"
              data-testid="admin-regenerate-slots"
            >
              Régénérer maintenant
            </button>
            <span className="text-xs text-slate-500 ml-2">
              Valeur actuelle : <strong>{slotDays} jours</strong> (~{Math.round(slotDays / 30 * 10) / 10} mois)
            </span>
          </div>
        </div>


        {/* Filters & Search */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilter("pending")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid="filter-pending"
              >
                <Clock className="w-4 h-4 inline mr-1" /> En attente
                {stats && <span className="ml-1 bg-amber-200 px-2 py-0.5 rounded-full text-xs">{stats.pending_practitioners}</span>}
              </button>
              <button
                onClick={() => setFilter("verified")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid="filter-verified"
              >
                <UserCheck className="w-4 h-4 inline mr-1" /> Vérifiés
              </button>
              <button
                onClick={() => setFilter("suspended")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === "suspended" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid="filter-suspended"
              >
                <Ban className="w-4 h-4 inline mr-1" /> Suspendus
              </button>
            </div>

            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un praticien..."
                className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:border-slate-400 outline-none w-full md:w-64"
                data-testid="search-practitioners"
              />
            </div>

            <button
              onClick={fetchData}
              className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
          </div>
        </div>

        {/* Practitioners List */}
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-slate-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredPractitioners.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Aucun praticien trouvé</h3>
            <p className="text-slate-500 mt-1">
              {filter === "pending" && "Pas de praticiens en attente de validation"}
              {filter === "verified" && "Aucun praticien vérifié"}
              {filter === "suspended" && "Aucun praticien suspendu"}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPractitioners.map(practitioner => (
              <PractitionerCard
                key={practitioner.id}
                practitioner={practitioner}
                onValidate={handleValidate}
                onReject={handleReject}
                onSuspend={handleSuspend}
                onReactivate={handleReactivate}
                loading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default {
  AdminProvider,
  AdminLoginPage,
  AdminDashboardPage,
  AdminForgotPasswordPage,
  AdminResetPasswordPage
};
